/**
 * AuditPortal.jsx
 * Telemetry Stream Controller Node Component Implementation
 */

import React, { useState, useEffect, useCallback } from 'react';
import './AuditPortal.css';
import { api } from '../../api/rapid-imlementation-workflow-api';
import { currentRole, isTabAllowed, CHROME_TABS } from './roleAccess';


export default function AuditPortal({ onTabChange }) {
  const role = currentRole();
  // Database Telemetry Context Collections
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState('Connecting database tables...');

  // Controlled Grid Filters State Object Pool
  const [filters, setFilters] = useState({
    quoteFilter: '',
    actorFilter: '',
    categoryFilter: '',
    limitFilter: '250',
    dateFilter: '',
    timeframeFilter: '',
    fromDateFilter: '',
    fromTimeFilter: '',
    toDateFilter: '',
    toTimeFilter: ''
  });

  // Structural Helpers
  const handleFilterChange = (field, val) => {
    setFilters(prev => ({ ...prev, [field]: val }));
  };

  const clearDateMode = (selectedMode) => {
    setFilters(prev => {
      const updated = { ...prev };
      if (selectedMode === 'date') {
        updated.timeframeFilter = '';
        updated.fromDateFilter = ''; updated.fromTimeFilter = '';
        updated.toDateFilter = ''; updated.toTimeFilter = '';
      } else if (selectedMode === 'timeframe') {
        updated.dateFilter = '';
        updated.fromDateFilter = ''; updated.fromTimeFilter = '';
        updated.toDateFilter = ''; updated.toTimeFilter = '';
      } else if (selectedMode === 'range') {
        updated.dateFilter = '';
        updated.timeframeFilter = '';
      }
      return updated;
    });
  };

  const compileQueryObject = useCallback(() => {
    const p = {};
    if (filters.quoteFilter.trim()) p.quoteNum = filters.quoteFilter.trim();
    if (filters.actorFilter) p.actorId = filters.actorFilter;
    if (filters.categoryFilter) p.category = filters.categoryFilter;
    if (filters.limitFilter) p.limit = filters.limitFilter;

    if (filters.timeframeFilter) {
      p.timeframe = filters.timeframeFilter;
    } else if (filters.dateFilter) {
      p.date = filters.dateFilter;
    } else {
      if (filters.fromDateFilter) {
        p.from = filters.fromTimeFilter ? `${filters.fromDateFilter}T${filters.fromTimeFilter}` : filters.fromDateFilter;
      }
      if (filters.toDateFilter) {
        p.to = filters.toTimeFilter ? `${filters.toDateFilter}T${filters.toTimeFilter}` : filters.toDateFilter;
      }
    }
    return p;
  }, [filters]);

  // Executive Data Query Pipelines
  const runTelemetryQuery = useCallback(async () => {
    setLoading(true);
    setStatusMessage('Querying database metrics data layer...');
    try {
      const activeQueries = compileQueryObject();
      const res = await api.fetchAuditEvents(activeQueries);
      const matchedEvents = res.events || [];
      setEvents(matchedEvents);
      setStatusMessage(matchedEvents.length ? 
        `${matchedEvents.length} audit event${matchedEvents.length === 1 ? '' : 's'} found.` : 
        'No transaction records matched the selection filter matrix.'
      );
    } catch (err) {
      setStatusMessage(`Execution error returning transaction streams: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [compileQueryObject]);

  const flushCriteriaFilters = () => {
    setFilters({
      quoteFilter: '', actorFilter: '', categoryFilter: '', limitFilter: '250',
      dateFilter: '', timeframeFilter: '', fromDateFilter: '', fromTimeFilter: '',
      toDateFilter: '', toTimeFilter: ''
    });
  };

  const executeDataTxtExport = () => {
    api.triggerAuditExportDownload(compileQueryObject());
  };

  // Initialization Scope Hooks Loop
  useEffect(() => {
      runTelemetryQuery();
   }, [runTelemetryQuery]);

  const getTargetText = (target) => {
  if (!target) return '-';
  
  // If the target completely matches the user object profile directly
  if (typeof target === 'object' && 'displayName' in target) {
    return target.displayName || target.id || 'User Entity';
  }
  
  return [target.type, target.label || target.id].filter(Boolean).map(item => 
    typeof item === 'object' ? (item.displayName || item.id || JSON.stringify(item)) : item
  ).join(': ') || '-';
};

  const isAlertError = statusMessage.startsWith('Execution error') || statusMessage.startsWith('Could not');

  return (
    <div className="audit-wrapper">
      {/* LOCAL INTEGRATION WINDOW CHROME */}
      <div id="chrome">
        <div className="chrome-logo"><span>SF</span> Sales Cloud</div>
        <div className="chrome-nav">
          {CHROME_TABS.filter(t => isTabAllowed(role, t.id)).map(t => (
            <button key={t.id} type="button" className={`chrome-nav-item ${t.id === 'audit' ? 'active' : ''}`} onClick={() => onTabChange(t.id)}>{t.label}</button>
          ))}
        </div>
        <div className="chrome-crumb">
          <span>Dashboard</span><span className="chrome-sep">/</span><b>Audit Log Trail Viewer</b>
        </div>
        <div className="chrome-actions">
          <button className="sf-btn" onClick={runTelemetryQuery}>Refresh</button>
          <button className="sf-btn" onClick={executeDataTxtExport}>Export TXT</button>
        </div>
      </div>

      <main className="shell">
        <div className="hero">
          <div>
            <h1>Audit Log</h1>
            <p>Review action and field-change events by quote, placeholder user, date, or Pacific-time range.</p>
          </div>
          <span className="pill">Pacific time</span>
        </div>

        {/* LOG CRITERIA COMPONENT */}
        <section className="card">
          <div className="card-hd">
            <div>
              <h2>Filters</h2>
              <p>Use one date mode at a time: exact date, timeframe, or date/time range.</p>
            </div>
          </div>
          <div className="card-bd">
            <div className="grid">
              <div className="field">
                <label>Quote</label>
                <input type="text" value={filters.quoteFilter} onChange={e => handleFilterChange('quoteFilter', e.target.value)} placeholder="Quote number" />
              </div>
              {/* <div className="field">
                <label>User</label>
                <select value={filters.actorFilter} onChange={e => handleFilterChange('actorFilter', e.target.value)}>
                  <option value="">All system profiles</option>
                  {users.map(u => (
                    <option key={u.id} value={u.id}>{u.displayName || u.id}</option>
                  ))}
                </select>
              </div> */}
              <div className="field">
                <label>Category</label>
                <select value={filters.categoryFilter} onChange={e => handleFilterChange('categoryFilter', e.target.value)}>
                  <option value="">All categories</option>
                  {['navigation', 'intake', 'quote', 'ai', 'signoff', 'checklist', 'field', 'matrix', 'audit', 'user'].map(cat => (
                    <option key={cat} value={cat}>{cat.charAt(0).toUpperCase() + cat.slice(1)}</option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label>Record Limit</label>
                <select value={filters.limitFilter} onChange={e => handleFilterChange('limitFilter', e.target.value)}>
                  <option value="100">100 records</option>
                  <option value="250">250 records</option>
                  <option value="500">500 records</option>
                  <option value="1000">1000 records</option>
                </select>
              </div>
              <div className="field">
                <label>Exact Calendar Date</label>
                <input type="date" value={filters.dateFilter} onChange={e => { handleFilterChange('dateFilter', e.target.value); clearDateMode('date'); }} />
              </div>
              <div className="field">
                <label>Relative Timeframe Window</label>
                <select value={filters.timeframeFilter} onChange={e => { handleFilterChange('timeframeFilter', e.target.value); clearDateMode('timeframe'); }}>
                  <option value="">No context wrapper</option>
                  <option value="today">Today</option>
                  <option value="yesterday">Yesterday</option>
                  <option value="last7">Last 7 days</option>
                  <option value="last30">Last 30 days</option>
                </select>
              </div>
              <div className="field">
                <label>Target Span: From Time</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 96px', gap: '6px' }}>
                  <input type="date" value={filters.fromDateFilter} onChange={e => { handleFilterChange('fromDateFilter', e.target.value); clearDateMode('range'); }} />
                  <input type="time" value={filters.fromTimeFilter} onChange={e => { handleFilterChange('fromTimeFilter', e.target.value); clearDateMode('range'); }} />
                </div>
              </div>
              <div className="field">
                <label>Target Span: To Time</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 96px', gap: '6px' }}>
                  <input type="date" value={filters.toDateFilter} onChange={e => { handleFilterChange('toDateFilter', e.target.value); clearDateMode('range'); }} />
                  <input type="time" value={filters.toTimeFilter} onChange={e => { handleFilterChange('toTimeFilter', e.target.value); clearDateMode('range'); }} />
                </div>
              </div>
            </div>
            <div className="toolbar">
              <button className="btn primary" onClick={runTelemetryQuery}>Apply Filters</button>
              <button className="btn" onClick={executeDataTxtExport}>Export TXT</button>
              <button className="btn" onClick={flushCriteriaFilters}>Reset Filters</button>
            </div>
          </div>
        </section>

        {/* LOG EVENTS DATA WRAPPER */}
        <section className="card">
          <div className="card-hd">
            <div>
              <h2>Audit Events</h2>
              <p>{statusMessage}</p>
            </div>
          </div>
          <div style={{ padding: '14px 20px 0' }}>
            <div className={`alert ${isAlertError ? 'amber' : 'blue'}`}>{statusMessage}</div>
          </div>

          {loading ? (
            <div className="empty">
              <strong>Querying database records data layer...</strong>
              <span>Please hold.</span>
            </div>
          ) : !events.length ? (
            <div className="empty">
              <strong>No telemetry instances visible.</strong>
              <span>{statusMessage}</span>
            </div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Pacific Time</th>
                    <th>User</th>
                    <th>Quote #</th>
                    <th>Executed Action</th>
                    <th>Target Scope</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((ev, index) => {
                    const actorName = ev.user;
                    return (
                      <tr key={ev.id || index}>
                        <td>
                          <div className="main-cell">{ev.pacificTime || ev.timestamp}</div>
                        </td>
                        <td>
                          <div className="main-cell">{actorName}</div>
                          <div className="sub-cell">{ev.role || ''}</div>
                        </td>
                        <td className="mono">{ev.quoteNum || '-'}</td>
                        <td>
                          <div className="main-cell">{ev.action || '-'}</div>
                          <div className="sub-cell">{ev.category || '-'}</div>
                        </td>
                        <td>{getTargetText(ev.target)}</td>
                        <td className="detail-cell">{ev.detail || '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}